/**
 * SEMANTIC LAYER SERVICE
 * 
 * Servicio principal que proporciona:
 * - Normalización de términos (ansiedad = nerviosismo = angustia)
 * - Expansión de búsquedas (mindfulness → respiración, meditación, presencia)
 * - Recomendaciones de tratamiento basadas en síntomas
 * - Auto-categorización de contenido
 * 
 * EVOLUCIÓN DINÁMICA:
 * - Combina ontología base (código) + extensiones dinámicas (Firestore)
 * - Soporta términos personalizados por terapeuta
 * - Trackea términos no encontrados para mejorar
 * - Auto-refresh cada 5 minutos
 * 
 * USO:
 * - RAG: Expande queries de búsqueda con conceptos relacionados
 * - Asistente IA: Entiende qué dice el usuario y qué recomendar
 * - Generador de prácticas: Detecta temas en contenido
 * - Indexación: Extrae conceptos al subir materiales
 */

import { Logger } from '@/core/utils';
import { 
  CONCEPT_ONTOLOGY, 
  Concept, 
  ConceptId,
  getConcept,
  getChildren,
  getAllDescendants,
  getRelatedConcepts,
  getAllSynonyms,
  getConceptPath
} from './ontology';
import { 
  SYMPTOM_TREATMENT_MAP,
  SymptomMapping,
  TreatmentRecommendation,
  getTreatmentsForSymptom,
  getTopTreatments,
  getTreatmentsForMultipleSymptoms,
  getEmergencyTreatments,
  checkRedFlags,
  findSymptomByTerm
} from './symptom-treatment-map';
import { semanticExtensionsService } from './semantic-extensions.service';

const logger = Logger.getLogger('SemanticLayerService');

// ============================================
// TIPOS
// ============================================

export interface NormalizationResult {
  originalTerm: string;
  normalizedConceptIds: ConceptId[];
  confidence: number;  // 0-1
}

export interface ExpandedQuery {
  originalTerms: string[];
  expandedConceptIds: ConceptId[];
  synonyms: string[];
  searchKeywords: string[];  // Para Firestore array-contains-any
}

export interface ContentAnalysis {
  detectedConcepts: Array<{
    conceptId: ConceptId;
    confidence: number;
    matches: string[];  // Términos del texto que matchearon
  }>;
  suggestedCategories: ConceptId[];
  suggestedTags: string[];
  detectedSymptoms: string[];
  recommendedTechniques: TreatmentRecommendation[];
}

export interface PracticeRecommendation {
  techniqueId: ConceptId;
  techniqueName: string;
  score: number;
  reasons: string[];
  evidenceLevel: string;
  duration?: number;
  difficulty: string;
}

// ============================================
// SERVICIO PRINCIPAL
// ============================================

class SemanticLayerService {
  
  // Cache para búsquedas frecuentes (ontología base)
  private synonymCache: Map<string, ConceptId[]> = new Map();
  
  // Estado de inicialización
  private initialized: boolean = false;
  private initPromise: Promise<void> | null = null;
  
  constructor() {
    // Pre-cachear sinónimos de ontología base (síncrono)
    this.buildSynonymCache();
  }
  
  // ============================================
  // INICIALIZACIÓN Y EVOLUCIÓN DINÁMICA
  // ============================================
  
  /**
   * Inicializa el servicio cargando extensiones dinámicas
   * Llamar al iniciar la aplicación
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;
    
    this.initPromise = this._doInitialize();
    await this.initPromise;
    this.initPromise = null;
  }
  
  private async _doInitialize(): Promise<void> {
    try {
      await semanticExtensionsService.initialize();
      this.initialized = true;
      logger.info('SemanticLayerService inicializado con extensiones dinámicas');
    } catch (error) {
      logger.warn('Error cargando extensiones, usando solo ontología base', { error });
      this.initialized = true; // Continuar con base solamente
    }
  }
  
  /**
   * Refresca las extensiones dinámicas
   * Se puede llamar periódicamente o bajo demanda
   */
  async refreshExtensions(): Promise<void> {
    try {
      await semanticExtensionsService.refreshCache();
      logger.debug('Extensiones refrescadas');
    } catch (error) {
      logger.warn('Error refrescando extensiones', { error });
    }
  }
  
  /**
   * Construye cache de sinónimos para búsquedas rápidas (ontología base)
   */
  private buildSynonymCache(): void {
    for (const [conceptId, concept] of Object.entries(CONCEPT_ONTOLOGY)) {
      // Agregar el label en español normalizado
      const labelNorm = concept.labelEs.toLowerCase();
      this.addToCache(labelNorm, conceptId);
      
      // Agregar cada sinónimo
      for (const synonym of concept.synonyms) {
        const synNorm = synonym.toLowerCase();
        this.addToCache(synNorm, conceptId);
      }
    }
    
    logger.info('Synonym cache built', { entries: this.synonymCache.size });
  }
  
  private addToCache(term: string, conceptId: string): void {
    const existing = this.synonymCache.get(term) || [];
    if (!existing.includes(conceptId)) {
      existing.push(conceptId);
      this.synonymCache.set(term, existing);
    }
  }
  
  // ============================================
  // NORMALIZACIÓN DE TÉRMINOS
  // ============================================
  
  /**
   * Normaliza un término a sus conceptos correspondientes
   * "nerviosismo" → ["manejo_ansiedad"]
   * "respiración" → ["respiracion_consciente"]
   * 
   * EVOLUCIÓN: Busca en ontología base + extensiones dinámicas
   */
  normalizeTerms(
    terms: string | string[],
    options?: {
      therapistId?: string;  // Para buscar términos del terapeuta
      context?: 'user_library' | 'therapist_library' | 'ai_assistant' | 'practice_search';
      trackUnmatched?: boolean;  // Trackear términos no encontrados
      userId?: string;
    }
  ): NormalizationResult[] {
    const termArray = Array.isArray(terms) ? terms : [terms];
    const results: NormalizationResult[] = [];
    
    for (const term of termArray) {
      const normalized = term.toLowerCase().trim();
      
      // 1. Búsqueda exacta en cache base
      if (this.synonymCache.has(normalized)) {
        results.push({
          originalTerm: term,
          normalizedConceptIds: this.synonymCache.get(normalized)!,
          confidence: 1.0
        });
        continue;
      }
      
      // 2. Búsqueda en extensiones dinámicas
      const extensionMatches = semanticExtensionsService.lookupTerm(
        normalized, 
        options?.therapistId
      );
      
      if (extensionMatches.length > 0) {
        results.push({
          originalTerm: term,
          normalizedConceptIds: extensionMatches,
          confidence: 0.95 // Un poco menos que base pero muy alto
        });
        continue;
      }
      
      // 3. Búsqueda parcial en cache base
      const partialMatches: ConceptId[] = [];
      Array.from(this.synonymCache.entries()).forEach(([cachedTerm, conceptIds]) => {
        if (cachedTerm.includes(normalized) || normalized.includes(cachedTerm)) {
          partialMatches.push(...conceptIds);
        }
      });
      
      if (partialMatches.length > 0) {
        results.push({
          originalTerm: term,
          normalizedConceptIds: Array.from(new Set(partialMatches)),
          confidence: 0.7
        });
        continue;
      }
      
      // 4. No encontrado - trackear para aprendizaje
      if (options?.trackUnmatched && options?.context) {
        // Fire-and-forget para no bloquear
        semanticExtensionsService.trackUnmatchedTerm(
          term,
          options.context,
          options.userId,
          options.therapistId
        ).catch(e => logger.debug('Error tracking unmatched term', { error: e }));
      }
      
      results.push({
        originalTerm: term,
        normalizedConceptIds: [],
        confidence: 0
      });
    }
    
    return results;
  }
  
  /**
   * Normaliza un término y devuelve solo los IDs de conceptos
   * Versión simple para retrocompatibilidad
   */
  normalizeToConceptIds(term: string, therapistId?: string): ConceptId[] {
    const results = this.normalizeTerms(term, { therapistId });
    return results.flatMap(r => r.normalizedConceptIds);
  }
  
  // ============================================
  // EXPANSIÓN DE BÚSQUEDAS
  // ============================================
  
  /**
   * Expande una query de búsqueda con conceptos relacionados
   * "ansiedad" → búsqueda en ansiedad + nervios + estrés + respiración...
   */
  expandQuery(query: string): ExpandedQuery {
    // Tokenizar query
    const terms = query.toLowerCase()
      .split(/[\s,;.]+/)
      .filter(t => t.length > 2);
    
    const allConceptIds: Set<ConceptId> = new Set();
    const allSynonyms: Set<string> = new Set();
    
    for (const term of terms) {
      // Normalizar término
      const normalized = this.normalizeToConceptIds(term);
      
      for (const conceptId of normalized) {
        allConceptIds.add(conceptId);
        
        // Agregar sinónimos del concepto
        const synonyms = getAllSynonyms(conceptId);
        synonyms.forEach(s => allSynonyms.add(s.toLowerCase()));
        
        // Agregar conceptos relacionados (no jerárquicos)
        const related = getRelatedConcepts(conceptId);
        related.forEach(r => {
          allConceptIds.add(r.id);
          getAllSynonyms(r.id).forEach(s => allSynonyms.add(s.toLowerCase()));
        });
        
        // Agregar hijos (técnicas específicas)
        const children = getChildren(conceptId);
        children.forEach(c => {
          allConceptIds.add(c.id);
        });
        
        // Agregar padre (categoría más amplia)
        const concept = getConcept(conceptId);
        if (concept?.parent) {
          allConceptIds.add(concept.parent);
        }
      }
    }
    
    // Generar keywords para Firestore (máximo 10)
    const searchKeywords = this.generateSearchKeywords(
      Array.from(allConceptIds), 
      Array.from(allSynonyms)
    );
    
    return {
      originalTerms: terms,
      expandedConceptIds: Array.from(allConceptIds),
      synonyms: Array.from(allSynonyms),
      searchKeywords
    };
  }
  
  /**
   * Genera keywords optimizadas para búsqueda en Firestore
   * Prioriza términos más relevantes
   */
  private generateSearchKeywords(
    conceptIds: ConceptId[], 
    synonyms: string[],
    maxKeywords: number = 10
  ): string[] {
    const keywords: Array<{ term: string; priority: number }> = [];
    
    // Prioridad alta: IDs de conceptos
    for (const id of conceptIds) {
      const concept = getConcept(id);
      if (concept) {
        keywords.push({ term: concept.labelEs.toLowerCase(), priority: 3 });
        // Solo el primer sinónimo de cada concepto
        if (concept.synonyms[0]) {
          keywords.push({ term: concept.synonyms[0].toLowerCase(), priority: 2 });
        }
      }
    }
    
    // Prioridad media: sinónimos cortos
    for (const syn of synonyms) {
      if (syn.length <= 15 && syn.split(' ').length <= 2) {
        keywords.push({ term: syn, priority: 1 });
      }
    }
    
    // Ordenar por prioridad y deduplicar
    const unique = new Map<string, number>();
    for (const kw of keywords) {
      if (!unique.has(kw.term) || unique.get(kw.term)! < kw.priority) {
        unique.set(kw.term, kw.priority);
      }
    }
    
    return Array.from(unique.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, maxKeywords)
      .map(([term]) => term);
  }
  
  // ============================================
  // ANÁLISIS DE CONTENIDO
  // ============================================
  
  /**
   * Analiza un texto y detecta conceptos, categorías y síntomas
   * Útil para auto-categorizar materiales subidos
   */
  analyzeContent(text: string): ContentAnalysis {
    const normalizedText = text.toLowerCase();
    const detectedConcepts: ContentAnalysis['detectedConcepts'] = [];
    const detectedSymptomIds: string[] = [];
    
    // Buscar matches de conceptos en el texto
    for (const [conceptId, concept] of Object.entries(CONCEPT_ONTOLOGY)) {
      const matches: string[] = [];
      
      // Buscar label
      if (normalizedText.includes(concept.labelEs.toLowerCase())) {
        matches.push(concept.labelEs);
      }
      
      // Buscar sinónimos
      for (const synonym of concept.synonyms) {
        if (normalizedText.includes(synonym.toLowerCase())) {
          matches.push(synonym);
        }
      }
      
      if (matches.length > 0) {
        // Calcular confianza basada en número de matches y nivel del concepto
        let confidence = Math.min(matches.length * 0.3, 0.9);
        // Los conceptos más específicos (nivel 3) tienen mayor confianza
        if (concept.level === 3) confidence += 0.1;
        if (concept.level === 2) confidence += 0.05;
        
        detectedConcepts.push({
          conceptId,
          confidence: Math.min(confidence, 1),
          matches: Array.from(new Set(matches))
        });
      }
    }
    
    // Ordenar por confianza
    detectedConcepts.sort((a, b) => b.confidence - a.confidence);
    
    // Buscar síntomas
    for (const [symptomId, mapping] of Object.entries(SYMPTOM_TREATMENT_MAP)) {
      // Buscar el síntoma
      if (normalizedText.includes(mapping.labelEs.toLowerCase())) {
        detectedSymptomIds.push(symptomId);
        continue;
      }
      
      // Buscar manifestaciones
      for (const manifestation of mapping.manifestations) {
        if (normalizedText.includes(manifestation.toLowerCase())) {
          detectedSymptomIds.push(symptomId);
          break;
        }
      }
    }
    
    // Generar categorías sugeridas (conceptos nivel 1 y 2 más frecuentes)
    const categoryCounts = new Map<ConceptId, number>();
    for (const detected of detectedConcepts) {
      const path = getConceptPath(detected.conceptId);
      for (const concept of path) {
        if (concept.level === 1 || concept.level === 2) {
          categoryCounts.set(
            concept.id, 
            (categoryCounts.get(concept.id) || 0) + detected.confidence
          );
        }
      }
    }
    
    const suggestedCategories = Array.from(categoryCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id]) => id);
    
    // Generar tags sugeridos (conceptos nivel 2 y 3)
    const suggestedTags = detectedConcepts
      .filter(d => {
        const concept = getConcept(d.conceptId);
        return concept && (concept.level === 2 || concept.level === 3);
      })
      .slice(0, 10)
      .map(d => getConcept(d.conceptId)!.labelEs);
    
    // Obtener técnicas recomendadas para los síntomas detectados
    const recommendedTechniques = detectedSymptomIds.length > 0
      ? getTreatmentsForMultipleSymptoms(detectedSymptomIds)
          .slice(0, 5)
          .map(t => getTreatmentsForSymptom(t.symptoms[0])
            .find(tr => tr.techniqueId === t.techniqueId)!)
          .filter(Boolean)
      : [];
    
    return {
      detectedConcepts: detectedConcepts.slice(0, 20),
      suggestedCategories,
      suggestedTags,
      detectedSymptoms: Array.from(new Set(detectedSymptomIds)),
      recommendedTechniques
    };
  }
  
  // ============================================
  // RECOMENDACIONES DE PRÁCTICAS
  // ============================================
  
  /**
   * Recomienda prácticas basadas en lo que dice el usuario
   * Útil para el asistente IA
   */
  recommendPractices(
    userInput: string,
    context?: {
      previousPractices?: string[];  // Prácticas que ya ha hecho
      preferredDuration?: number;     // Duración preferida en minutos
      preferredDifficulty?: 'easy' | 'medium' | 'hard';
      isEmergency?: boolean;          // Si necesita algo rápido
    }
  ): PracticeRecommendation[] {
    const analysis = this.analyzeContent(userInput);
    const recommendations: PracticeRecommendation[] = [];
    
    // Si es emergencia, priorizar técnicas rápidas
    if (context?.isEmergency && analysis.detectedSymptoms.length > 0) {
      for (const symptomId of analysis.detectedSymptoms) {
        const emergency = getEmergencyTreatments(symptomId);
        for (const treatment of emergency) {
          this.addRecommendation(recommendations, treatment, symptomId, 1.5);
        }
      }
    }
    
    // Agregar tratamientos para síntomas detectados
    for (const symptomId of analysis.detectedSymptoms) {
      const treatments = getTopTreatments(symptomId, 5);
      for (const treatment of treatments) {
        this.addRecommendation(recommendations, treatment, symptomId, 1.0);
      }
    }
    
    // Agregar técnicas de conceptos detectados
    for (const detected of analysis.detectedConcepts) {
      const concept = getConcept(detected.conceptId);
      if (concept?.level === 3) {
        // Es una técnica específica
        recommendations.push({
          techniqueId: detected.conceptId,
          techniqueName: concept.labelEs,
          score: detected.confidence * 0.8,
          reasons: [`Detectado en tu mensaje: "${detected.matches[0]}"`],
          evidenceLevel: concept.scientificEvidence || 'moderada',
          difficulty: 'medium'
        });
      }
    }
    
    // Filtrar y ordenar
    let filtered = this.filterRecommendations(recommendations, context);
    
    // Ordenar por score
    filtered.sort((a, b) => b.score - a.score);
    
    // Deduplicar por techniqueId
    const seen = new Set<string>();
    filtered = filtered.filter(r => {
      if (seen.has(r.techniqueId)) return false;
      seen.add(r.techniqueId);
      return true;
    });
    
    return filtered.slice(0, 5);
  }
  
  private addRecommendation(
    recommendations: PracticeRecommendation[],
    treatment: TreatmentRecommendation,
    symptomId: string,
    multiplier: number
  ): void {
    const concept = getConcept(treatment.techniqueId);
    const symptom = SYMPTOM_TREATMENT_MAP[symptomId];
    
    if (!concept) return;
    
    const existing = recommendations.find(r => r.techniqueId === treatment.techniqueId);
    
    if (existing) {
      existing.score += treatment.effectSize * multiplier * 0.5;
      existing.reasons.push(`También ayuda con ${symptom?.labelEs || symptomId}`);
    } else {
      recommendations.push({
        techniqueId: treatment.techniqueId,
        techniqueName: concept.labelEs,
        score: treatment.effectSize * multiplier,
        reasons: [`Efectivo para ${symptom?.labelEs || symptomId}`],
        evidenceLevel: treatment.evidenceLevel,
        duration: treatment.duration,
        difficulty: treatment.difficulty
      });
    }
  }
  
  private filterRecommendations(
    recommendations: PracticeRecommendation[],
    context?: {
      previousPractices?: string[];
      preferredDuration?: number;
      preferredDifficulty?: 'easy' | 'medium' | 'hard';
    }
  ): PracticeRecommendation[] {
    return recommendations.filter(rec => {
      // Penalizar prácticas ya hechas (pero no eliminar)
      if (context?.previousPractices?.includes(rec.techniqueId)) {
        rec.score *= 0.5;
      }
      
      // Filtrar por duración si especificada
      if (context?.preferredDuration && rec.duration) {
        if (rec.duration > context.preferredDuration * 1.5) {
          rec.score *= 0.7;
        }
      }
      
      // Filtrar por dificultad si especificada
      if (context?.preferredDifficulty && rec.difficulty !== context.preferredDifficulty) {
        rec.score *= 0.8;
      }
      
      return rec.score > 0.2;
    });
  }
  
  // ============================================
  // DETECCIÓN DE EMERGENCIAS
  // ============================================
  
  /**
   * Detecta si hay señales de alerta en el input del usuario
   * Devuelve red flags si los encuentra
   */
  detectRedFlags(userInput: string): {
    hasRedFlags: boolean;
    flags: string[];
    urgencyLevel: 'low' | 'medium' | 'high' | 'critical';
  } {
    const normalizedInput = userInput.toLowerCase();
    const detectedFlags: string[] = [];
    
    // Palabras clave de urgencia crítica
    const criticalKeywords = [
      'suicidarme', 'suicidio', 'quitarme la vida', 'matarme',
      'acabar con todo', 'no quiero vivir', 'hacerme daño',
      'autolesion', 'cortarme', 'morirme'
    ];
    
    for (const keyword of criticalKeywords) {
      if (normalizedInput.includes(keyword)) {
        detectedFlags.push(`Mención de: ${keyword}`);
      }
    }
    
    if (detectedFlags.length > 0) {
      return {
        hasRedFlags: true,
        flags: detectedFlags,
        urgencyLevel: 'critical'
      };
    }
    
    // Analizar síntomas y sus red flags
    const analysis = this.analyzeContent(userInput);
    const symptomRedFlags = checkRedFlags(analysis.detectedSymptoms);
    
    if (symptomRedFlags.length > 0) {
      return {
        hasRedFlags: true,
        flags: symptomRedFlags,
        urgencyLevel: 'high'
      };
    }
    
    return {
      hasRedFlags: false,
      flags: [],
      urgencyLevel: 'low'
    };
  }
  
  // ============================================
  // UTILIDADES PARA RAG
  // ============================================
  
  /**
   * Genera keywords para indexar un documento en Firestore
   * Útil al subir materiales
   */
  generateIndexKeywords(text: string): string[] {
    const analysis = this.analyzeContent(text);
    const keywords: Set<string> = new Set();
    
    // Agregar conceptos detectados
    for (const detected of analysis.detectedConcepts) {
      const concept = getConcept(detected.conceptId);
      if (concept) {
        keywords.add(concept.labelEs.toLowerCase());
        // Solo los primeros 2 sinónimos para no inflar
        concept.synonyms.slice(0, 2).forEach(s => 
          keywords.add(s.toLowerCase())
        );
      }
    }
    
    // Agregar tags sugeridos
    analysis.suggestedTags.forEach(t => keywords.add(t.toLowerCase()));
    
    // Agregar síntomas
    for (const symptomId of analysis.detectedSymptoms) {
      const mapping = SYMPTOM_TREATMENT_MAP[symptomId];
      if (mapping) {
        keywords.add(mapping.labelEs.toLowerCase());
      }
    }
    
    return Array.from(keywords).slice(0, 30);  // Límite razonable
  }
  
  /**
   * Obtiene información de un concepto para mostrar al usuario
   */
  getConceptInfo(conceptId: ConceptId): {
    name: string;
    description?: string;
    parent?: string;
    children: string[];
    related: string[];
    evidence?: string;
  } | null {
    const concept = getConcept(conceptId);
    if (!concept) return null;
    
    const parent = concept.parent ? getConcept(concept.parent) : null;
    const children = getChildren(conceptId);
    const related = getRelatedConcepts(conceptId);
    
    return {
      name: concept.labelEs,
      description: concept.description,
      parent: parent?.labelEs,
      children: children.map(c => c.labelEs),
      related: related.map(c => c.labelEs),
      evidence: concept.scientificEvidence
    };
  }

  // ============================================
  // GESTIÓN DE EXTENSIONES (EVOLUCIÓN DINÁMICA)
  // ============================================
  
  /**
   * Agrega un término personalizado para un terapeuta
   * Permite que cada terapeuta use su propia terminología
   */
  async addTherapistTerm(
    therapistId: string,
    term: string,
    mapsToConceptId: ConceptId,
    options?: {
      mapsToPracticeId?: string;
      notes?: string;
    }
  ): Promise<string> {
    return semanticExtensionsService.addTherapistTerm({
      therapistId,
      term,
      mapsToConceptId,
      mapsToPracticeId: options?.mapsToPracticeId,
      notes: options?.notes,
    });
  }
  
  /**
   * Obtiene los términos personalizados de un terapeuta
   */
  async getTherapistTerms(therapistId: string) {
    return semanticExtensionsService.getTherapistTerms(therapistId);
  }
  
  /**
   * Registra que una práctica fue útil para un contexto dado
   * Mejora las recomendaciones futuras
   */
  async recordPracticeEffectiveness(
    userInput: string,
    practiceId: string,
    conceptId: ConceptId,
    wasEffective: boolean
  ): Promise<void> {
    await semanticExtensionsService.recordAssociation(
      userInput,
      conceptId,
      practiceId,
      wasEffective
    );
  }
  
  /**
   * 🧠 CLASIFICACIÓN AUTOMÁTICA DE PRÁCTICAS
   * 
   * Analiza el título, descripción e instrucciones de una práctica
   * y devuelve los tags semánticos, síntomas objetivo y técnicas.
   * 
   * Esto permite indexar las prácticas para RAG eficiente.
   */
  classifyPractice(practice: {
    title: string;
    description: string;
    instructions?: string;
    category?: string;
  }): {
    semanticTags: string[];
    targetSymptoms: string[];
    techniques: string[];
  } {
    // Combinar todo el texto de la práctica
    const fullText = [
      practice.title,
      practice.description,
      practice.instructions || '',
      practice.category || ''
    ].join(' ');
    
    // Analizar con el Semantic Layer
    const analysis = this.analyzeContent(fullText);
    
    // Extraer tags de los conceptos detectados
    const semanticTags = analysis.detectedConcepts
      .filter(c => c.confidence > 0.3)
      .map(c => c.conceptId);
    
    // Extraer síntomas que puede tratar (desde las recomendaciones)
    const targetSymptoms = analysis.detectedSymptoms;
    
    // Extraer técnicas recomendadas
    const techniques = analysis.recommendedTechniques
      .slice(0, 5)
      .map(t => t.techniqueId);
    
    logger.debug('Practice classified', {
      title: practice.title,
      semanticTags,
      targetSymptoms,
      techniques
    });
    
    return {
      semanticTags,
      targetSymptoms,
      techniques
    };
  }
  
  /**
   * 🎯 RETRIEVAL: Buscar prácticas relevantes para un mensaje
   * 
   * Dado un mensaje del usuario y una lista de prácticas clasificadas,
   * devuelve las prácticas ordenadas por relevancia semántica.
   */
  retrieveRelevantPractices<T extends {
    id: string;
    practiceData: {
      title: string;
      description: string;
      instructions?: string;  // 📝 Contenido completo para que el agente lea
      category: string;
      duration?: number;
      semanticTags?: string[];
      targetSymptoms?: string[];
      techniques?: string[];
    };
  }>(userMessage: string, practices: T[]): Array<T & { relevanceScore: number; matchReasons: string[] }> {
    // Analizar el mensaje del usuario
    const userAnalysis = this.analyzeContent(userMessage);
    
    // Conceptos y síntomas detectados en el mensaje
    const userConcepts = new Set(userAnalysis.detectedConcepts.map(c => c.conceptId));
    const userSymptoms = new Set(userAnalysis.detectedSymptoms);
    const recommendedTechniques = new Set(userAnalysis.recommendedTechniques.map(t => t.techniqueId));
    
    // Expandir con sinónimos
    const expanded = this.expandQuery(userMessage);
    const expandedTerms = new Set([...expanded.synonyms, ...expanded.searchKeywords].map(t => t.toLowerCase()));
    
    // Puntuar cada práctica
    const scored = practices.map(practice => {
      let score = 0;
      const matchReasons: string[] = [];
      
      const practiceTags = new Set(practice.practiceData.semanticTags || []);
      const practiceSymptoms = new Set(practice.practiceData.targetSymptoms || []);
      const practiceTechniques = new Set(practice.practiceData.techniques || []);
      
      // Match por conceptos semánticos
      for (const concept of userConcepts) {
        if (practiceTags.has(concept)) {
          score += 10;
          matchReasons.push(`Concepto: ${concept}`);
        }
      }
      
      // Match por síntomas
      for (const symptom of userSymptoms) {
        if (practiceSymptoms.has(symptom)) {
          score += 8;
          matchReasons.push(`Síntoma: ${symptom}`);
        }
      }
      
      // Match por técnicas recomendadas
      for (const technique of recommendedTechniques) {
        if (practiceTechniques.has(technique)) {
          score += 6;
          matchReasons.push(`Técnica: ${technique}`);
        }
      }
      
      // Match por términos expandidos en título/descripción
      const practiceText = `${practice.practiceData.title} ${practice.practiceData.description}`.toLowerCase();
      for (const term of expandedTerms) {
        if (practiceText.includes(term)) {
          score += 2;
        }
      }
      
      return {
        ...practice,
        relevanceScore: score,
        matchReasons
      };
    });
    
    // Filtrar solo las que tienen score > 0 y ordenar por relevancia
    return scored
      .filter(p => p.relevanceScore > 0)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
  }

  /**
   * Obtiene estadísticas de la Semantic Layer
   */
  async getStats() {
    return semanticExtensionsService.getStats();
  }
  
  /**
   * Obtiene la configuración actual de la Semantic Layer
   */
  getConfig() {
    return semanticExtensionsService.getConfig();
  }
  
  /**
   * Verifica si el servicio está inicializado
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// ============================================
// SINGLETON EXPORT
// ============================================

export const semanticLayerService = new SemanticLayerService();

// Re-exportar funciones útiles de los módulos
export {
  getConcept,
  getChildren,
  getAllDescendants,
  getConceptPath,
  getRelatedConcepts,
  getAllSynonyms,
  getTreatmentsForSymptom,
  getTopTreatments,
  getEmergencyTreatments,
  checkRedFlags,
  findSymptomByTerm
};

// Re-exportar servicio de extensiones para admin
export { semanticExtensionsService };

